import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Alert } from '@mui/material';
import LoadingSpinner from '../components/LoadingSpinner.js';
import { API_BASE_URL } from '../api/client.js';
import type { IScoreCard, IQuickWin, IEvaluationResult } from '../../shared/types.js';
import { SHARE_CONTACT } from './share/shareContact.js';
import {
  resolveScoreCardTemplateId,
  loadScoreCardTemplateSource,
  normalizeScoreCardHtmlTemplate,
  type ScoreCardHtmlTemplateId,
} from './share/scoreCardTemplates.js';
import { renderScoreCardHtml, type SharedScoreCardData } from './share/renderScoreCardHtml.js';

const POLL_INTERVAL_MS = 5000;
const STABILIZE_DELAY_MS = 20_000;

interface SharedData {
  score_card: IScoreCard & { Scan?: { url: string; user?: { name: string } } };
  evaluation: IEvaluationResult | null;
  quick_wins: IQuickWin[];
  disclaimer: string | null;
  /** Aus Scan-Template-Konfiguration; ?template= in der URL ueberschreibt. */
  score_card_html_template?: string | null;
}

export default function SharedScoreCardPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const templateQuery = searchParams.get('template');

  const [renderedHtml, setRenderedHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stabilizing, setStabilizing] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stabilizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedRef = useRef(false);
  const wasPollingRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Ungültiger Share-Link.');
      return;
    }

    let cancelled = false;
    completedRef.current = false;
    wasPollingRef.current = false;

    let cachedTemplateId: ScoreCardHtmlTemplateId | null = null;
    let cachedTplSource: string | null = null;

    const renderTemplate = async (payload: SharedData, scanPending: boolean, evaluating = false) => {
      const effectiveTemplateId =
        cachedTemplateId ??
        (templateQuery !== null && templateQuery !== ''
          ? resolveScoreCardTemplateId(templateQuery)
          : normalizeScoreCardHtmlTemplate(payload.score_card_html_template));
      cachedTemplateId = effectiveTemplateId;

      const tplSource = cachedTplSource ?? await loadScoreCardTemplateSource(effectiveTemplateId);
      cachedTplSource = tplSource;

      return renderScoreCardHtml(
        tplSource,
        payload as SharedScoreCardData,
        effectiveTemplateId,
        SHARE_CONTACT,
        { scanPending, evaluating },
      );
    };

    /**
     * Lädt die Share-Daten und rendert die Score Card.
     * Gibt true zurück, wenn weitergepollt werden soll (Scan noch nicht fertig).
     */
    const tryLoad = async (): Promise<boolean> => {
      try {
        const shareRes = await fetch(`${API_BASE_URL}/share/${token}`);
        const shareJson = await shareRes.json();

        if (cancelled) return false;

        if (!shareJson.success) {
          const status = shareRes.status;
          if (status === 401 || status === 403 || status === 404 || status === 410) {
            setError(shareJson.error?.message ?? 'Fehler beim Laden');
            return false;
          }
          return true;
        }

        const payload = shareJson.data as SharedData;
        const scanPending = !payload.evaluation;

        if (!scanPending && wasPollingRef.current) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          const pendingPayload: SharedData = { ...payload, evaluation: null, quick_wins: [] };
          const evalHtml = await renderTemplate(pendingPayload, false, true);
          if (!cancelled) setRenderedHtml(evalHtml);

          setStabilizing(true);
          stabilizeTimerRef.current = setTimeout(async () => {
            if (cancelled) return;
            const finalRes = await fetch(`${API_BASE_URL}/share/${token}`);
            const finalJson = await finalRes.json();
            if (cancelled) return;
            if (finalJson.success) {
              const finalPayload = finalJson.data as SharedData;
              const finalHtml = await renderTemplate(finalPayload, false);
              if (!cancelled) setRenderedHtml(finalHtml);
            }
            completedRef.current = true;
            setStabilizing(false);
          }, STABILIZE_DELAY_MS);
          return false;
        }

        const html = await renderTemplate(payload, scanPending);

        if (cancelled) return false;

        if (!html.trim()) {
          setError('Score Card konnte nicht gerendert werden.');
          return false;
        }

        setRenderedHtml(html);

        if (!scanPending) {
          completedRef.current = true;
          return false;
        }
        wasPollingRef.current = true;
        return true;
      } catch {
        return !cancelled;
      }
    };

    (async () => {
      setLoading(true);
      setError(null);
      setRenderedHtml(null);

      const keepPolling = await tryLoad();

      if (cancelled) return;
      setLoading(false);

      if (keepPolling) {
        intervalRef.current = setInterval(async () => {
          if (completedRef.current) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            return;
          }
          const stop = !(await tryLoad());
          if (stop && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }, POLL_INTERVAL_MS);
      }
    })();

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (stabilizeTimerRef.current) {
        clearTimeout(stabilizeTimerRef.current);
        stabilizeTimerRef.current = null;
      }
    };
  }, [token, templateQuery]);

  if (loading) return <LoadingSpinner message="Score Card wird geladen..." />;

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>{error}</Alert>
      </Box>
    );
  }

  if (!renderedHtml) return null;

  return (
    <div
      className="share-score-card-host"
      style={{
        minHeight: '100vh',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
      // eslint-disable-next-line react/no-danger -- bewusst: HTML-Templates aus Datei + escapte API-Daten
      dangerouslySetInnerHTML={{ __html: renderedHtml }}
    />
  );
}
